import React, { type ReactNode, type KeyboardEvent, useEffect, useRef } from "react";

// 3rd-party Libraries
import { useFlags } from "launchdarkly-react-client-sdk";

import { MenuItem, type MenuItemOptions } from "primereact/menuitem";
import { Menubar } from "primereact/menubar";
import { BreadCrumb } from "primereact/breadcrumb";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";

// Toolkit Imports
import { useUserContext } from "../../contexts/User/UserContextProvider";
import { useNavContext } from "../../contexts/Nav/NavContextProvider";
import { useBreadcrumbContextOptional } from "../../contexts/Breadcrumb/BreadcrumbProvider";
import { useSettingsContext } from "../../contexts/Settings/SettingsContextProvider";
import { INTERNAL_ROUTES } from "../../../constants/routes";
import { FEATURES } from "../../../constants/features";
import { useSmartNavigation } from "../../../helpers/navigation";

// Sub-Components
import { NavSearchForm } from "./NavSearchBar/NavSearchBar";
import { Notifications } from "./Notifications/Notifications";
import { NavLogo } from "./NavLogo/NavLogo";
import { ProfileMenu } from "./ProfileMenu/ProfileMenu";

// Styles
import S from "./UserNavBar.module.scss";

export interface UserNavBarProps {
  breadcrumbs?: MenuItem[];
  dialogTemplate?: (props: {
    showDialog: boolean;
    setShowDialog: (value: boolean) => void;
    onConfirm: () => void;
  }) => ReactNode;
  navbarRef?: React.RefObject<HTMLDivElement>;
}

export function UserNavBar({ breadcrumbs, dialogTemplate, navbarRef }: UserNavBarProps) {
  const { legacyLink, enableAtlasAssessmentsUi, aspenTenantAdminChat } = useFlags();
  const {
    // Basic state flags
    isLoading,
    isAuthenticated,

    // SJ Admin specific flags
    isSjAdmin,
    impersonatedEmail,

    // User and tenant info
    user,
    selectedTenant,
    isTenantAdmin,

    // UI state
    isTourVisible,

    // State setters and toggles
    setUser,
    toggleTourVisibility,
    sjAdminLeaveTenant,
    sjAdminStopImpersonation,
  } = useUserContext();

  const { settings, isFeatureEnabled } = useSettingsContext();
  const { leaderboard: showLeaderboard, catalog: showCatalog, contacts } = settings || {};

  // Calculate some derived flags
  const showChampionPassport =
    isAuthenticated &&
    !!user?.railsProfile?.hasAccessToDojoActivities &&
    isFeatureEnabled(FEATURES.CHAMPION_PASSPORT);

  const showUserOptions = isAuthenticated && (!isSjAdmin || !!impersonatedEmail);

  // Try to get breadcrumb context (new system) or nav context (legacy)
  const breadcrumbCtx = useBreadcrumbContextOptional();
  const navContext = useNavContext();

  const showConfirmation = !!dialogTemplate;
  const { navigateTo, showDialog, setShowDialog, confirmNavigation } = useSmartNavigation({
    showConfirmation,
  });

  // WCAG fix: PrimeReact Menubar renders <ul role="menu"> for every item's submenu,
  // even when there are no children. Empty role="menu" elements are flagged as broken ARIA menus.
  const menubarNavRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const nav = menubarNavRef.current;
    if (!nav) return;
    nav.querySelectorAll<HTMLUListElement>('ul.p-submenu-list[role="menu"]').forEach(ul => {
      if (ul.children.length === 0) {
        ul.removeAttribute("role");
      }
    });
  });

  const displayBreadcrumbs = breadcrumbCtx
    ? breadcrumbCtx.display === "navbar"
      ? breadcrumbCtx.breadcrumbs
      : undefined
    : (navContext?.breadcrumbs ?? breadcrumbs);

  // Dynamic home item: uses BreadcrumbProvider's homeItem if available, otherwise defaults to Learn
  const defaultHome = {
    label: INTERNAL_ROUTES.Learn.name,
    command: () => navigateTo(INTERNAL_ROUTES.Learn.route),
  };
  const homeItem = breadcrumbCtx?.homeItem ?? defaultHome;

  // Event handlers
  const handleLeaveTenant = async () => {
    await sjAdminLeaveTenant();
  };

  // WCAG 2.2 AA: PrimeReact Menubar sets tabindex="-1" on all <a> tags,
  // so we use custom templates with tabIndex={0} to make items tabbable.

  // Template for simple nav links (no submenu)
  function NavMenuLink(
    item: MenuItem,
    options: MenuItemOptions,
    route: string,
    openInNewTab?: boolean
  ) {
    return (
      <a
        className={options.className}
        tabIndex={0}
        onClick={e => {
          e.preventDefault();
          navigateTo(route, openInNewTab);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigateTo(route, openInNewTab);
          }
        }}
      >
        <span className={options.labelClassName}>{item.label}</span>
      </a>
    );
  }

  // Template for dropdown parent items (has submenu children)
  function NavMenuDropdown(item: MenuItem, options: MenuItemOptions) {
    return (
      <a
        className={options.className}
        tabIndex={0}
        onClick={options.onClick}
        onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            options.onClick(e as unknown as React.SyntheticEvent);
          }
        }}
      >
        <span className={options.labelClassName}>{item.label}</span>
        <i className="pi pi-angle-down p-submenu-icon" />
      </a>
    );
  }

  /**
   * Main navigation items
   */
  const items: MenuItem[] = [
    {
      label: INTERNAL_ROUTES.Learn.name,
      visible: showUserOptions,
      ...(showChampionPassport
        ? {
            template: (item: MenuItem, options: MenuItemOptions) => NavMenuDropdown(item, options),
            items: [
              {
                label: "Dashboard",
                template: (item: MenuItem, options: MenuItemOptions) =>
                  NavMenuLink(item, options, INTERNAL_ROUTES.Learn.route),
              },
              {
                label: "Champion Passport",
                template: (item: MenuItem, options: MenuItemOptions) =>
                  NavMenuLink(item, options, INTERNAL_ROUTES.Champions.route),
              },
            ],
          }
        : {
            template: (item: MenuItem, options: MenuItemOptions) =>
              NavMenuLink(item, options, INTERNAL_ROUTES.Learn.route),
          }),
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.assessments.name,
      visible: showUserOptions && enableAtlasAssessmentsUi,
      template: (item: MenuItem, options: MenuItemOptions) =>
        NavMenuLink(item, options, INTERNAL_ROUTES.Learn.routes.assessments.route),
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.catalog.name,
      visible: showUserOptions && showCatalog,
      template: (item: MenuItem, options: MenuItemOptions) =>
        NavMenuLink(item, options, INTERNAL_ROUTES.Learn.routes.catalog.route),
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.leaderboard.name,
      visible: showLeaderboard,
      template: (item: MenuItem, options: MenuItemOptions) =>
        NavMenuLink(item, options, INTERNAL_ROUTES.Learn.routes.leaderboard.route),
    },
    {
      label: INTERNAL_ROUTES.Admin.name,
      visible: isTenantAdmin,
      template: (item: MenuItem, options: MenuItemOptions) =>
        NavMenuLink(item, options, INTERNAL_ROUTES.Admin.route),
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.aspenChat.name,
      visible: isTenantAdmin && !!aspenTenantAdminChat,
      template: (item: MenuItem, options: MenuItemOptions) =>
        NavMenuLink(item, options, INTERNAL_ROUTES.Learn.routes.aspenChat.route),
    },
    {
      label: "Legacy Portal",
      visible: legacyLink && !isSjAdmin,
      template: function LegacyPortalLink(item: MenuItem, options: MenuItemOptions) {
        const handleLegacyNav = () => {
          const legacyUrl = document.location.origin.endsWith(".dev")
            ? "https://my.securityjourney.dev"
            : "https://my.securityjourney.com";
          window.location.href = `${legacyUrl}?clearAtlasPreference=true`;
        };
        return (
          <a
            className={options.className}
            tabIndex={0}
            onClick={e => {
              e.preventDefault();
              handleLegacyNav();
            }}
            onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLegacyNav();
              }
            }}
          >
            <span className={options.labelClassName}>{item.label}</span>
          </a>
        );
      },
    },
  ];

  // Calculate how many visible main nav items there are
  const visibleItemCount = items.reduce(
    (count, item) => (item.visible === false ? count : count + 1),
    0
  );

  const end = (
    <>
      {isSjAdmin && (
        <div className="flex items-center gap-2">
          <Badge value={`SJ Admin (${selectedTenant})`} severity="info" />
          {!impersonatedEmail && <Button label="Leave Tenant" onClick={handleLeaveTenant} />}
        </div>
      )}
      {showUserOptions && (
        <>
          <NavSearchForm menuItemCount={visibleItemCount} />
          <Notifications />
          <ProfileMenu
            user={user}
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
            isTourVisible={isTourVisible}
            toggleTourVisibility={toggleTourVisibility}
            navigateTo={navigateTo}
            setUser={setUser}
            selectedTenant={selectedTenant}
            isSjAdmin={isSjAdmin}
            impersonatedEmail={impersonatedEmail}
            sjAdminLeaveTenant={sjAdminLeaveTenant}
            sjAdminStopImpersonation={sjAdminStopImpersonation}
            contacts={contacts}
          />
        </>
      )}
    </>
  );

  if (displayBreadcrumbs) {
    return (
      <>
        <nav
          ref={navbarRef}
          className={`${S.menubar} ${S.breadcrumbContainer}`}
          aria-label="Main navigation"
        >
          <div className={S.breadcrumbStart}>
            <NavLogo
              navigateTo={navigateTo}
              thumbLogoUrl={settings?.thumbLogoUrl}
              addMargin={true}
            />
            <BreadCrumb
              model={displayBreadcrumbs}
              home={homeItem}
              pt={{
                root: { "aria-label": "Breadcrumb" },
                action: { className: "body-sm" },
                separator: { role: "listitem" },
              }}
            />
          </div>
          <div className={S.breadcrumbEnd}>
            <Notifications />
            <ProfileMenu
              user={user}
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
              isTourVisible={isTourVisible}
              toggleTourVisibility={toggleTourVisibility}
              navigateTo={navigateTo}
              setUser={setUser}
              selectedTenant={selectedTenant}
              isSjAdmin={isSjAdmin}
              impersonatedEmail={impersonatedEmail}
              sjAdminLeaveTenant={sjAdminLeaveTenant}
              sjAdminStopImpersonation={sjAdminStopImpersonation}
              contacts={contacts}
            />
          </div>
        </nav>
        {dialogTemplate?.({
          showDialog,
          setShowDialog,
          onConfirm: confirmNavigation,
        })}
      </>
    );
  }

  return (
    <nav
      ref={(node: HTMLElement | null) => {
        menubarNavRef.current = node;
        if (navbarRef)
          (navbarRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node as HTMLDivElement | null;
      }}
      aria-label="Main navigation"
    >
      <Menubar
        start={<NavLogo navigateTo={navigateTo} thumbLogoUrl={settings?.thumbLogoUrl} />}
        model={items}
        end={end}
        className={S.menubar}
        pt={{
          menu: { className: "gap-1" },
          content: { className: "body-sm" },
          action: { className: "px-3" },
          button: { className: S.menubarButton },
          icon: { className: S.menubarIcon },
          end: { className: S.menubarEnd },
        }}
      />
      {dialogTemplate?.({
        showDialog,
        setShowDialog,
        onConfirm: confirmNavigation,
      })}
    </nav>
  );
}
